import React, { useState, useEffect } from 'react';
import AdminLayout from '../AdminLayout.tsx';
import { adminFetch } from '../../../utils/adminFetch.ts';
import { formatBytes } from '../../../utils/formatBytes.ts';

interface CollectionStats {
  size: number;
  count: number;
  avgObjSize: number;
}

interface DatabaseStatistics {
  user_count: number;
  active_api_key_count: number;
  collection_stats: Record<string, CollectionStats>;
}

const DatabaseStats: React.FC = () => {
  const [stats, setStats] = useState<DatabaseStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDatabaseStats = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Use our new adminFetch utility to ensure correct headers
        const data = await adminFetch<DatabaseStatistics>('/database/stats');
        console.log("Successfully received database stats:", data);
        setStats(data);
      } catch (err: any) {
        console.error('Error fetching database stats:', err.message);
        setError(err.message || "Failed to load database statistics");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDatabaseStats();
  }, []);

  return (
    <AdminLayout title="Database Statistics">
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-base rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-dark mb-2">Total Users</h3>
              <p className="text-3xl font-bold">{stats?.user_count || 0}</p>
            </div>
            
            <div className="bg-base rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-dark mb-2">Active API Keys</h3>
              <p className="text-3xl font-bold">{stats?.active_api_key_count || 0}</p>
            </div>
            
            <div className="bg-base rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-dark mb-2">Total Collections</h3>
              <p className="text-3xl font-bold">
                {stats?.collection_stats ? Object.keys(stats.collection_stats).length : 0}
              </p>
            </div>
          </div>
          
          {/* Collection details table */}
          <div className="bg-base rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-base">
              <h3 className="text-lg font-bold">Collection Details</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-base">
                <thead className="bg-gray-base">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Collection Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Documents Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Average Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Total Size
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-base divide-y divide-gray-base">
                  {stats?.collection_stats && Object.entries(stats.collection_stats).map(([name, collStats]) => (
                    <tr key={name} className="hover:bg-gray-light">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {collStats.count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatBytes(collStats.avgObjSize)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatBytes(collStats.size)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default DatabaseStats;
